/**
 * Per-frame suffix for every `sessionStorage` key this app owns.
 *
 * `sessionStorage` is scoped to the top-level tab, not to each frame, so two
 * same-origin iframes in one page share a single store. Embedding two
 * masqueraded sessions side by side therefore had them rehydrate from one
 * `persist:root`: the second frame booted holding the first user's session,
 * `UnAuthRoute` saw `isLogin` already true and redirected to `/dashboard`
 * before the sign-in screen could redeem its own token, so both frames showed
 * the first user.
 *
 * `window.name` is the one piece of per-frame state that survives in-frame
 * navigation and refresh, which makes it the natural place to park an instance
 * id — each frame gets a private key (`persist:root:ps-frame-…`) while a
 * reload inside that frame still finds its own session.
 *
 * Resolved once at module evaluation, before the store is created.
 */
const PREFIX = 'ps-frame-';

function resolveNamespace(): string {
  /* Unframed app keeps the historical key, so sessions already in storage
     survive the deploy rather than being silently abandoned. */
  if (window.self === window.top) return '';

  /* A host that names its frames has already given this one an identity worth
     more than a random id — adopt it instead of overwriting it. Must be unique
     per frame to isolate anything. */
  if (window.name && !window.name.startsWith(PREFIX)) {
    return `:${window.name}`;
  }

  if (!window.name) {
    /* `randomUUID` needs a secure context and a modern engine; the legacy
       bundle (`@vitejs/plugin-legacy`) may have neither, and uniqueness only
       has to hold across the frames of one page. */
    const id =
      globalThis.crypto?.randomUUID?.() ??
      `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    window.name = `${PREFIX}${id}`;
  }

  return `:${window.name}`;
}

const STORAGE_NAMESPACE = resolveNamespace();

/** Suffixes a `sessionStorage` key so it cannot collide with a sibling frame. */
export function namespacedKey(key: string): string {
  return `${key}${STORAGE_NAMESPACE}`;
}

export default STORAGE_NAMESPACE;
